import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ShieldAlert, Users, ArrowRight, Loader2, Check, AlertTriangle } from "lucide-react";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { useAgencyNavigate } from "../lib/agency";
import {
  requestNotificationPermission,
  getPushToken,
  getPlatform,
} from "../lib/native";

const STORAGE_GROUP = "ts_my_group";

export default function GroupJoin() {
  const { groupId } = useParams();
  const navigate = useAgencyNavigate();
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [selectedName, setSelectedName] = useState("");
  const [email, setEmail] = useState("");
  const [joining, setJoining] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const loadGroup = async () => {
    setLoading(true);
    setNotFound(false);
    setLoadFailed(false);
    try {
      const { data } = await api.get(`/groups/${groupId}`);
      setGroup(data);
    } catch (e) {
      // Antes cualquier error (incluida una falla de red pasajera) se
      // mostraba como "grupo no encontrado" — un mensaje que le dice a la
      // persona que pida un link nuevo cuando en realidad solo hubo que
      // reintentar.
      if (e?.response?.status === 404) setNotFound(true);
      else setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadGroup(); }, [groupId]);

  const names = group
    ? [group.creator_name, ...(group.members || []).map((m) => m.name)].filter(Boolean)
    : [];

  const join = async () => {
    if (!selectedName) return;
    setErr("");
    setJoining(true);
    try {
      const perm = await requestNotificationPermission();
      if (perm !== "granted") {
        setErr(
          "Necesitas permitir las notificaciones para recibir alertas si otro miembro activa el SOS."
        );
        setJoining(false);
        return;
      }
      try {
        const fcmToken = await getPushToken();
        if (fcmToken) {
          await api.post(`/groups/${groupId}/register-device`, {
            fcm_token: fcmToken,
            member_name: selectedName,
            email,
            platform: getPlatform(),
          });
        }
      } catch (e) {
        console.error("No se pudo registrar el dispositivo para push:", e);
        // seguimos igual: al menos podrá activar su propio SOS
      }
      localStorage.setItem(
        STORAGE_GROUP,
        // myName: quién es ESTE dispositivo dentro del grupo — sin esto, la
        // app no tenía forma de distinguir "yo lo mandé" de "alguien más del
        // grupo lo mandó" y siempre asumía que fue quien creó el grupo.
        JSON.stringify({ group: { ...group, sos_unlocked: true }, myName: selectedName })
      );
      setDone(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || "No se pudo completar el registro");
    } finally {
      setJoining(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <section className="max-w-lg mx-auto px-4 py-24 text-center">
          <Loader2 className="w-6 h-6 animate-spin mx-auto text-neutral-500" />
        </section>
      </Layout>
    );
  }

  if (notFound) {
    return (
      <Layout>
        <section className="max-w-lg mx-auto px-4 py-24 text-center fade-up">
          <ShieldAlert className="w-10 h-10 mx-auto text-neutral-600 mb-4" strokeWidth={1.4} />
          <h1 className="font-display text-2xl mb-2">Grupo no encontrado</h1>
          <p className="text-neutral-400 text-sm">
            El enlace de invitación puede haber caducado o estar mal copiado. Pídele a quien
            creó el grupo que te comparta el enlace de nuevo.
          </p>
        </section>
      </Layout>
    );
  }

  if (loadFailed) {
    return (
      <Layout>
        <section className="max-w-lg mx-auto px-4 py-24 text-center fade-up" data-testid="join-load-error">
          <AlertTriangle className="w-10 h-10 mx-auto text-[#FFB020] mb-4" strokeWidth={1.4} />
          <h1 className="font-display text-2xl mb-2">No se pudo cargar el grupo</h1>
          <p className="text-neutral-400 text-sm mb-6">Revisa tu conexión e intenta de nuevo.</p>
          <button
            onClick={loadGroup}
            className="px-6 py-3 rounded-full border border-[#FFB020]/40 text-[#FFB020] hover:bg-[#FFB020]/10"
          >
            Reintentar
          </button>
        </section>
      </Layout>
    );
  }

  if (done) {
    return (
      <Layout>
        <section className="max-w-lg mx-auto px-4 py-16 fade-up">
          <div className="glass rounded-3xl p-10 border border-[var(--brand-cyan,#FF85AC)]/30 text-center" data-testid="join-done">
            <div className="inline-flex w-14 h-14 rounded-full bg-[var(--brand-cyan,#FF85AC)]/20 border border-[var(--brand-cyan,#FF85AC)] items-center justify-center mb-5">
              <Check className="w-6 h-6 text-[var(--brand-cyan,#FF85AC)]" />
            </div>
            <h1 className="font-display text-2xl mb-2">¡Listo, {selectedName}!</h1>
            <p className="text-neutral-400 mb-6 text-sm">
              Quedaste protegido dentro de <b className="text-white">{group.name}</b>. Vas a
              recibir una notificación si alguien del grupo activa el SOS.
            </p>
            <button
              onClick={() => navigate("/sos")}
              data-testid="join-go-sos-btn"
              className="bg-[#FF3B30] text-white px-7 py-3 rounded-full font-semibold hover:bg-[#FF5147] danger-glow"
            >
              Ir al Botón SOS
            </button>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <section className="max-w-lg mx-auto px-4 py-16 fade-up">
        <div className="glass rounded-3xl p-10 border border-white/10">
          <div className="flex items-center gap-3 mb-5">
            <Users className="w-5 h-5 text-[var(--brand-cyan,#FF85AC)]" strokeWidth={1.5} />
            <p className="text-[10px] uppercase tracking-[0.3em] text-[var(--brand-cyan,#FF85AC)]">
              Invitación a grupo SOS
            </p>
          </div>
          <h1 className="font-display text-3xl font-light mb-2">{group.name}</h1>
          <p className="text-neutral-400 mb-7 text-sm">
            ¿Cuál de estos nombres eres tú? Elige el tuyo para activar tu protección en este
            dispositivo.
          </p>

          <div className="flex flex-col gap-2.5 mb-6" data-testid="join-name-list">
            {names.map((n) => (
              <button
                key={n}
                onClick={() => setSelectedName(n)}
                className={`text-left px-5 py-3.5 rounded-2xl border transition ${
                  selectedName === n
                    ? "border-[var(--brand-cyan,#FF85AC)] bg-[var(--brand-cyan,#FF85AC)]/10 text-white"
                    : "border-white/10 hover:border-white/25 text-neutral-300"
                }`}
              >
                {n}
              </button>
            ))}
          </div>

          <div className="mb-6">
            <label className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 block mb-2">Tu correo (opcional)</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="tu@correo.com"
              data-testid="join-email-input"
              className="w-full bg-transparent border-b border-white/15 focus:border-[var(--brand-cyan,#FF85AC)] outline-none py-2 text-white"
            />
            <p className="text-[10px] text-neutral-600 mt-1.5">Te mandamos ahí el aviso legal del SOS.</p>
          </div>

          {err && <p className="text-sm text-[#FF3B30] mb-4" data-testid="join-error">{err}</p>}

          <button
            onClick={join}
            disabled={!selectedName || joining}
            data-testid="join-confirm-btn"
            className="w-full inline-flex items-center justify-center gap-2 bg-[var(--brand-cyan,#FF85AC)] text-black px-7 py-3.5 rounded-full font-semibold hover:bg-[#FF9DC0] disabled:opacity-40 disabled:cursor-not-allowed cyan-glow"
          >
            {joining ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Activando…</>
            ) : (
              <>Activar mi protección <ArrowRight className="w-4 h-4" /></>
            )}
          </button>

          <p className="text-[11px] text-neutral-600 mt-5 text-center">
            ¿No estás en la lista? Pídele a quien creó el grupo que te agregue primero.
          </p>
        </div>
      </section>
    </Layout>
  );
}
